/*
 * am-storefront-sort.js — reorder the collection grid by "most recently updated".
 *
 * Why this exists
 * ---------------
 * Shopify's Liquid caps collection.products at 250 per paginate call and does
 * not expose a metafield sort key. On /collections/all (1,411 products) and
 * /collections/aircraft-parts (1,153), Shopify picks page 1 by created_at
 * before any Liquid runs, so a listing on page 3 that was renewed today can
 * never move onto page 1 by Liquid alone.
 *
 * The Storefront API does expose a top-level products() query with
 * sortKey: UPDATED_AT across the entire catalogue. Every portal action
 * (renewal, update, new) rewrites product metafields, which bumps the
 * product's updated_at. So UPDATED_AT is a true "new + renewed + updated"
 * ranking without any extra portal work.
 *
 * How it works
 * ------------
 * 1. On page load, read the collection handle from the current URL.
 * 2. Call Storefront API: products(sortKey: UPDATED_AT, query: "tag:market-X")
 *    returning the first N product handles in the correct order.
 * 3. Reorder the already-rendered DOM cards in am-listing-grid to match.
 *    Cards not in the response (page 2+ territory) stay in their original
 *    position after the sorted ones.
 *
 * This means:
 *   - No markup changes — existing am-listing-card renders are reused.
 *   - Only runs on the default "newest" sort; explicit price/title sorts
 *     from the user are left alone.
 *   - Fails open: on token error, 429 or network issue, the page keeps the
 *     server-rendered order (which is still valid, just not metafield-sorted).
 */
(function () {
  'use strict';

  var CFG = window.AM_STOREFRONT_SORT || {};
  var API_VERSION = '2024-10';
  var MAX_SORT = 250; // Storefront API hard limit per call

  // Public Storefront API access token. Prefer an explicitly configured token,
  // but fall back to the one Shopify injects into every page via the
  // #shopify-features JSON block (used for predictive search). That token is
  // always present and valid for the unauthenticated Storefront API, so the
  // feature works with no theme setting and never goes stale.
  function readPageToken() {
    try {
      var el = document.getElementById('shopify-features');
      if (!el) return null;
      var data = JSON.parse(el.textContent || '{}');
      return data.accessToken || null;
    } catch (e) {
      return null;
    }
  }

  var TOKEN = CFG.token || readPageToken();
  var DOMAIN = CFG.domain || (window.Shopify && window.Shopify.shop) || '5h1eep-af.myshopify.com';

  // Only run on collection or search pages, and only for the default sort.
  var path = window.location.pathname;
  if (!/^\/collections\/[^\/]+\/?$/.test(path) && path !== '/search') return;
  var sortParam = new URL(window.location.href).searchParams.get('sort_by');
  if (sortParam && sortParam !== 'created-descending' && sortParam !== 'manual') return;

  // Need a token to call Storefront API.
  if (!TOKEN) return;

  // Derive the tag query from the URL path.
  // /collections/aircraft -> tag:market-aircraft
  // /collections/all      -> (no tag filter, all markets)
  // /collections/aircraft-parts -> tag:market-parts-products (handle differs from tag)
  var HANDLE_TO_TAG = {
    'aircraft': 'market-aircraft',
    'avionics': 'market-avionics',
    'engines': 'market-engines',
    'aircraft-parts': 'market-parts-products',
    'aviation-real-estate': 'market-real-estate',
    'aviation-services': 'market-services'
  };
  var m = path.match(/^\/collections\/([^\/]+)/);
  var handle = m ? m[1] : null;
  var tagQuery = '';
  if (handle && HANDLE_TO_TAG[handle]) {
    tagQuery = 'tag:' + HANDLE_TO_TAG[handle] + ' AND ';
  }
  // Everything (all-listings and search) is just status filter.
  var query = tagQuery + 'status:active';

  var grid = document.getElementById('AmListingGrid');
  if (!grid) return;

  // Build the GraphQL query. We ask for only `handle` — the DOM already has
  // the full card. We fetch MAX_SORT to cover page 1 reorder even if the
  // display size (48) changes later.
  var gql = {
    query:
      'query sortRecent($q: String!, $n: Int!) {' +
        'products(first: $n, sortKey: UPDATED_AT, reverse: true, query: $q) {' +
          'nodes { handle }' +
        '}' +
      '}',
    variables: { q: query, n: MAX_SORT }
  };

  fetch('https://' + DOMAIN + '/api/' + API_VERSION + '/graphql.json', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Shopify-Storefront-Access-Token': TOKEN
    },
    body: JSON.stringify(gql)
  })
    .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
    .then(function (data) {
      if (!data || !data.data || !data.data.products) return;
      var ordered = data.data.products.nodes.map(function (n) { return n.handle; });
      var domHandles = reorder(grid, ordered);
      // Shopify renders only the first ~250 products (by created_at) before any
      // JS runs, so a listing renewed today but created long ago is never in the
      // DOM and reorder alone can't lift it. Inject those missing top-ranked
      // cards by fetching the same am-listing-card markup server-side.
      injectMissing(grid, ordered, domHandles);
    })
    .catch(function () { /* fail open — leave the server order in place */ });

  // The server interleaves an in-grid ad slot every AD_INTERVAL product cards
  // (am-listing-grid-chunk.liquid uses `modulo: 12`). Reordering must preserve
  // that cadence, otherwise all the ad slots clump together.
  var AD_INTERVAL = 12;

  function isAdSlot(el) {
    return el.classList && el.classList.contains('am-ingrid-ad');
  }

  function reorder(gridEl, ordered) {
    // Partition current children into: product cards (by handle), ad slots, and
    // anything else (dividers etc.). Products get re-sorted; ads get re-spread;
    // "other" is kept in place at the end as a safety net.
    var cards = Array.prototype.slice.call(gridEl.children);
    var byHandle = {};
    var productEls = [];
    var adEls = [];
    var otherEls = [];
    for (var i = 0; i < cards.length; i++) {
      var el = cards[i];
      if (isAdSlot(el)) { adEls.push(el); continue; }
      var a = el.querySelector('a[href*="/products/"]');
      var hm = a ? (a.getAttribute('href') || '').match(/\/products\/([^\/\?#]+)/) : null;
      if (hm) {
        if (!byHandle[hm[1]]) byHandle[hm[1]] = el;
        productEls.push({ handle: hm[1], el: el });
      } else {
        otherEls.push(el);
      }
    }

    // Build the product sequence in API order first, then any product cards not
    // in the API response (keeps them, just after the ranked ones).
    var sortedProducts = [];
    var used = Object.create(null);
    for (var j = 0; j < ordered.length; j++) {
      var pe = byHandle[ordered[j]];
      if (pe && !used[ordered[j]]) { sortedProducts.push(pe); used[ordered[j]] = true; }
    }
    for (var k = 0; k < productEls.length; k++) {
      if (!used[productEls[k].handle]) { sortedProducts.push(productEls[k].el); used[productEls[k].handle] = true; }
    }

    // Re-emit products, dropping an ad slot back in every AD_INTERVAL products
    // (same cadence the server used), until we run out of ad slots.
    var frag = document.createDocumentFragment();
    var adIdx = 0;
    for (var p = 0; p < sortedProducts.length; p++) {
      frag.appendChild(sortedProducts[p]);
      if (adIdx < adEls.length && (p + 1) % AD_INTERVAL === 0) {
        frag.appendChild(adEls[adIdx++]);
      }
    }
    // Any leftover ad slots (grid shorter than ads*interval) go at the end.
    while (adIdx < adEls.length) frag.appendChild(adEls[adIdx++]);
    // Preserve any other non-product elements at the very end.
    for (var o = 0; o < otherEls.length; o++) frag.appendChild(otherEls[o]);

    gridEl.appendChild(frag);
    return byHandle; // handles already present in the DOM
  }

  // How many missing top-ranked cards to pull in. These are rare (old created_at
  // + recent renewal), so a small cap covers the real cases without hammering the
  // theme with hundreds of fetches. Overridable via AM_STOREFRONT_SORT.injectMax.
  var INJECT_MAX = (CFG.injectMax != null) ? CFG.injectMax : 24;
  var INJECT_CONCURRENCY = 4;

  function injectMissing(gridEl, ordered, present) {
    // Only the TOP slice of the API ranking matters for "on page 1": if a
    // renewed listing isn't in the first INJECT_MAX of the global order, it
    // wouldn't be on page 1 anyway. So look only at the top INJECT_MAX ranked
    // handles and inject whichever of those are missing from the DOM. This also
    // bounds fetches to the handful that actually belong near the top.
    var topSlice = ordered.slice(0, INJECT_MAX);
    var missing = [];
    for (var i = 0; i < topSlice.length; i++) {
      if (!present[topSlice[i]]) missing.push(topSlice[i]);
    }
    if (missing.length === 0) return;

    // Fetch each missing card's HTML (server-rendered am-listing-card via the
    // product.amcard template), preserving API order. Bounded concurrency.
    var results = new Array(missing.length);
    var next = 0, active = 0, done = 0;

    function pump() {
      while (active < INJECT_CONCURRENCY && next < missing.length) {
        (function (idx, handle) {
          active++;
          fetchCard(handle)
            .then(function (el) { results[idx] = el; })
            .catch(function () { results[idx] = null; })
            .then(function () {
              active--; done++;
              if (done === missing.length) place();
              else pump();
            });
        })(next, missing[next]);
        next++;
      }
    }

    function place() {
      // Add the injected cards to the grid, then run one final reorder over the
      // WHOLE grid (existing + injected) so everything interleaves in the true
      // API order — not two separately-sorted blocks.
      var frag = document.createDocumentFragment();
      for (var i = 0; i < results.length; i++) {
        if (results[i]) frag.appendChild(results[i]);
      }
      if (!frag.childNodes.length) return;
      gridEl.insertBefore(frag, gridEl.firstChild);
      reorder(gridEl, ordered);
    }

    pump();
  }

  function fetchCard(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '?view=amcard', {
      headers: { 'Accept': 'text/html' },
      credentials: 'same-origin'
    })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then(function (html) {
        var tpl = document.createElement('template');
        tpl.innerHTML = html.trim();
        // The template renders a single <article.am-listing-card>; grab it.
        var card = tpl.content.querySelector('.am-listing-card');
        if (!card) throw new Error('no card in response');
        card.setAttribute('data-am-injected', '1');
        return card;
      });
  }
})();
